import React, { useRef } from 'react';
import { PlacementPhoto } from '@libs/models';
import { PlacementPhotoPreview } from './PlacementPhotoPreview';
import { AlertCircle, Camera, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dropzone, DropzoneEmptyState } from 'src/components/ui/shadcn-io/dropzone/index';

interface PlacementPhotoUploadProps {
  photos: PlacementPhoto[];
  onUploadFiles: (files: File[]) => void;
  onDeletePhoto: (fileName: string) => void;
  maxPhotos?: number;
  disabled?: boolean;
  isUploading?: boolean;
  uploadingFiles?: File[];
  uploadingFileStates?: Map<string, 'uploading' | 'success' | 'error'>;
  className?: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const PlacementPhotoUpload: React.FC<PlacementPhotoUploadProps> = ({
  photos,
  onUploadFiles,
  onDeletePhoto,
  maxPhotos = 5,
  disabled = false,
  isUploading = false,
  uploadingFiles = [],
  uploadingFileStates,
  className,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const totalPhotos = photos.length + uploadingFiles.length;
  const canAddMore = totalPhotos < maxPhotos;
  const hasPhotos = photos.length > 0 || uploadingFiles.length > 0;

  const handleDrop = (acceptedFiles: File[]) => {
    const remaining = maxPhotos - totalPhotos;
    const filesToUpload = acceptedFiles.slice(0, remaining);
    if (filesToUpload.length > 0) {
      onUploadFiles(filesToUpload);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      handleDrop(files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="text-sm font-medium label">Placement Photos</span>
          <span className="text-xs text-muted bg-slate-100 rounded-full px-2 py-0.5">
            {totalPhotos}/{maxPhotos}
          </span>
        </div>
      </div>

      {/* Dropzone - Show when no photos exist */}
      {!hasPhotos && canAddMore && (
        <Dropzone
          maxFiles={maxPhotos}
          maxSize={MAX_FILE_SIZE}
          accept={{
            'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.heic', '.heif'],
          }}
          onDrop={handleDrop}
          disabled={disabled}
          className="border-dashed border hover:border-primary/50 transition-colors"
        >
          <DropzoneEmptyState>
            <div className="flex flex-col items-center justify-center space-y-2 py-2">
              <div className="rounded-full p-2.5 bg-slate-100">
                <Camera className="h-5 w-5 text-muted" />
              </div>
              <div className="space-y-0.5 text-center">
                <p className="text-sm font-medium text-text-primary">Upload placement photos</p>
                <p className="text-xs text-muted">
                  Drag & drop or click &middot; PNG, JPG, WebP up to 5 MB
                </p>
              </div>
            </div>
          </DropzoneEmptyState>
        </Dropzone>
      )}

      {/* Warning if at max capacity */}
      {totalPhotos >= maxPhotos && (
        <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
          <p className="text-xs text-amber-700">
            Maximum of {maxPhotos} photos reached. Remove a photo to add more.
          </p>
        </div>
      )}

      {/* Photos Grid */}
      {hasPhotos && (
        <div className="grid grid-cols-3 gap-2">
          {/* Existing Photos */}
          {photos.map((photo) => (
            <PlacementPhotoPreview
              key={photo.fileName}
              photo={photo}
              onRemove={!disabled ? () => onDeletePhoto(photo.fileName) : undefined}
            />
          ))}

          {/* Uploading Files */}
          {uploadingFiles.map((file, index) => {
            const uploadState = uploadingFileStates?.get(file.name);
            return (
              <PlacementPhotoPreview
                key={`uploading-${index}`}
                photo={file}
                isUploading={uploadState === 'uploading' || isUploading}
                uploadError={uploadState === 'error'}
              />
            );
          })}

          {/* Add More Tile */}
          {canAddMore && !disabled && (
            <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors">
              <Plus className="h-5 w-5 text-gray-400" />
              <span className="text-xs text-gray-400 mt-1">Add</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif"
                multiple
                onChange={handleFileInputChange}
                className="hidden"
              />
            </label>
          )}
        </div>
      )}
    </div>
  );
};
